import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { DatePicker } from 'antd-mobile'
import { getPhoneOrderRecord, updatePhoneOrderStatus } from '@/api/airportTransfer'
import { Toast } from 'antd-mobile'
import { useTranslation } from '@/i18n'
import callImg from "@/assets/images/shouji2.png"
import luyinImg from "@/assets/images/luyin.png"
import './index.scss'

const CallOrder = () => {
  const history = useHistory()
  const t = useTranslation()
  const [activeTab, setActiveTab] = useState('pending') // pending: 待服务, finished: 已结束
  const [activeDate, setActiveDate] = useState('all') // all, today, tomorrow, recent3, more
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    pageNo: 1,
    pageSize: 10000,
    total: 0
  })

  // 计算用车时间范围
  const getTimeRange = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    if (activeDate === 'all') {
      // 全部时间，不传时间限制或传一个很大的范围
      return {
        startTime: null,
        endTime: null
      }
    } else if (activeDate === 'today') {
      const endOfDay = new Date(today)
      endOfDay.setHours(23, 59, 59, 999)
      return {
        startTime: today.getTime(),
        endTime: endOfDay.getTime()
      }
    } else if (activeDate === 'tomorrow') {
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)
      const endOfTomorrow = new Date(tomorrow)
      endOfTomorrow.setHours(23, 59, 59, 999)
      return {
        startTime: tomorrow.getTime(),
        endTime: endOfTomorrow.getTime()
      }
    } else if (activeDate === 'recent3') {
      // 近3日：从今天开始往后3天
      const endDate = new Date(today)
      endDate.setDate(endDate.getDate() + 2)
      endDate.setHours(23, 59, 59, 999)
      return {
        startTime: today.getTime(),
        endTime: endDate.getTime()
      }
    } else if (activeDate === 'more') {
      const selected = new Date(selectedDate)
      selected.setHours(0, 0, 0, 0)
      const endOfSelected = new Date(selected)
      endOfSelected.setHours(23, 59, 59, 999)
      return {
        startTime: selected.getTime(),
        endTime: endOfSelected.getTime()
      }
    }
    return {
      startTime: null,
      endTime: null
    }
  }

  // 根据departureTime判断订单是否在时间范围内
  const isOrderInTimeRange = (departureTime, timeRange) => {
    // 如果是"全部"时间（没有时间限制），则所有订单都显示，包括departureTime为空的
    if (timeRange.startTime === null && timeRange.endTime === null) return true
    
    // 如果departureTime为空，且不是"全部"筛选，则不显示
    if (!departureTime) return false
    
    // 将departureTime转换为时间戳进行比较
    // departureTime可能是字符串格式，如 "2025-10-16 14:30"
    let orderTimestamp
    if (typeof departureTime === 'string') {
      orderTimestamp = new Date(departureTime.replace(/-/g, '/')).getTime()
    } else {
      orderTimestamp = departureTime
    }
    
    // 检查是否在时间范围内
    if (timeRange.startTime !== null && orderTimestamp < timeRange.startTime) {
      return false
    }
    if (timeRange.endTime !== null && orderTimestamp > timeRange.endTime) {
      return false
    }
    return true
  }

  // 获取订单列表
  const fetchOrders = async () => {
    setLoading(true)
    try {
      const params = {
        pageNo: pagination.pageNo,
        pageSize: pagination.pageSize,
        countFlag: true,
        orderSource: 2, // 2-电话下单
      }

      const response = await getPhoneOrderRecord(params)

      if (response && response.data && response.data.code === 200) {
        const allOrders = response.data.data.datas || []
        
        // 格式化价格
        allOrders.forEach(e => {
          e.price = e.price ? (e.price / 100).toFixed(2) : '0.00'
        })
        
        const timeRange = getTimeRange()
        
        // 根据订单状态筛选订单
        // 待服务: orderStatus 为 1(呼叫中), 2(接驾中), 3(司机到达上车点), 4(乘客上车)
        // 已结束: orderStatus 为 0(已取消), 5(已完成), 6(司机已收款)
        let filteredOrders = allOrders.filter(order => {
          // 先根据订单状态筛选
          let statusMatch = false
          if (activeTab === 'pending') {
            statusMatch = [1, 2, 3, 4].includes(order.orderStatus)
          } else if (activeTab === 'finished') {
            statusMatch = [0, 5, 6].includes(order.orderStatus)
          }
          
          // 再根据departureTime时间范围筛选
          const timeMatch = isOrderInTimeRange(order.departureTime, timeRange)
          
          return statusMatch && timeMatch
        })
        
        setOrders(filteredOrders)
        
        setPagination({
          ...pagination,
          pageNo: response.data.data.currentPage || pagination.pageNo,
          total: response.data.data.total || 0
        })
      } else if (response.data && response.data.errorMsg) {
        console.error(response.data.errorMsg)
        Toast.offline(response.data.errorMsg, 2)
      }
    } catch (error) {
      console.error('获取订单列表失败', error)
      Toast.offline(t('airportTransfer.error.fetchFailed'), 2)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [activeTab, activeDate, selectedDate])

  // 更新订单状态处理
  const handleUpdateOrderStatus = async (orderId, status) => {
    const statusTextMap = {
      3: t('callOrder.action.arrivedPickup'),
      4: t('callOrder.action.passengerBoarded'),
      5: t('callOrder.action.orderEnd')
    }
    
    const actionText = statusTextMap[status] || t('callOrder.action.update')
    
    Toast.loading(t('callOrder.message.processing'), 0, () => {}, true)
    try {
      const response = await updatePhoneOrderStatus(orderId, status.toString())
      Toast.hide()
      if (response && response.data && response.data.code === 200) {
        Toast.success(`${actionText}${t('common.success')}`)
        // 刷新订单列表
        fetchOrders()
      } else {
        console.error('更新订单状态失败', response)
        Toast.fail(t('callOrder.message.updateFailed') + ': ' + (response?.data?.errorMsg || t('airportTransfer.error.unknown')), 2)
      }
    } catch (error) {
      console.error('更新订单状态出错', error)
      Toast.hide()
      Toast.fail(t('callOrder.message.updateError'), 2)
    }
  }

  // 播放语音
  const handlePlayVoice = (voiceUrl, e) => {
    e.stopPropagation()
    if (voiceUrl) {
      const audio = new Audio(voiceUrl)
      audio.play().catch(error => {
        console.error('播放语音失败:', error)
        Toast.fail(t('callOrder.voice.playFailed'), 2)
      })
    } else {
      Toast.info(t('callOrder.voice.noVoice'), 2)
    }
  }

  // 点击订单卡片跳转到详情页
  const handleOrderClick = (orderId) => {
    history.push(`/callOrder/detail/${orderId}`)
  }

  // 切换顶部标签
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    // 重置分页
    setPagination({
      pageNo: 1,
      pageSize: 10,
      total: 0
    })
  }

  // 切换日期筛选
  const handleDateChange = (date) => {
    setActiveDate(date)
    if (date === 'more') {
      setShowDatePicker(true)
    }
    // 重置分页
    setPagination({
      pageNo: 1,
      pageSize: 10,
      total: 0
    })
  }

  // 日期选择器确认
  const handleDateConfirm = (date) => {
    setSelectedDate(date)
    setShowDatePicker(false)
  }

  // 格式化日期显示
  const getDateLabel = () => {
    if (activeDate === 'all') return t('callOrder.dateFilter.all')
    if (activeDate === 'today') return t('callOrder.dateFilter.today')
    if (activeDate === 'tomorrow') return t('callOrder.dateFilter.tomorrow')
    if (activeDate === 'recent3') return t('callOrder.dateFilter.recent3')
    if (activeDate === 'more') {
      const year = selectedDate.getFullYear()
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0')
      const day = String(selectedDate.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }

  // 获取订单状态文本
  const getOrderStatusText = (status) => {
    const statusMap = {
      0: t('orderCard.status.cancelled'),
      1: t('orderCard.status.calling'),
      2: t('orderCard.status.pickingUp'),
      3: t('orderCard.status.arrivedPickup'),
      4: t('orderCard.status.passengerBoarded'),
      5: t('orderCard.status.completed'),
      6: t('orderCard.status.driverCollected'),
    }
    return statusMap[status] || t('orderCard.status.unknown')
  }

  return (
    <div className="call-order">
      {/* 顶部标签切换 */}
      <div className="tabs-header">
        <div 
          className={`tab-item ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabChange('pending')}
        >
          {t('callOrder.tab.pending')}
        </div>
        <div 
          className={`tab-item ${activeTab === 'finished' ? 'active' : ''}`}
          onClick={() => handleTabChange('finished')}
        >
          {t('callOrder.tab.finished')}
        </div>
      </div>

      {/* 日期筛选 */}
      <div className="date-filter">
        <div 
          className={`date-item ${activeDate === 'all' ? 'active' : ''}`}
          onClick={() => handleDateChange('all')}
        >
          {t('callOrder.dateFilter.all')}
        </div>
        <div 
          className={`date-item ${activeDate === 'today' ? 'active' : ''}`}
          onClick={() => handleDateChange('today')}
        >
          {t('callOrder.dateFilter.today')}
        </div>
        <div 
          className={`date-item ${activeDate === 'tomorrow' ? 'active' : ''}`}
          onClick={() => handleDateChange('tomorrow')}
        >
          {t('callOrder.dateFilter.tomorrow')}
        </div>
        <div 
          className={`date-item ${activeDate === 'recent3' ? 'active' : ''}`}
          onClick={() => handleDateChange('recent3')}
        >
          {t('callOrder.dateFilter.recent3')}
        </div>
        <div 
          className={`date-item ${activeDate === 'more' ? 'active' : ''}`}
          onClick={() => handleDateChange('more')}
        >
          <i className="iconfont icon-calendar"></i>
          {t('callOrder.dateFilter.more')}
        </div>
      </div>

      {/* 订单列表 */}
      <div className="order-list">
        {loading ? (
          <div className="loading">{t('common.loading')}</div>
        ) : orders.length > 0 ? (
          orders.map(order => (
            <div key={order.orderId} className="order-card">
              <div className="order-header" onClick={() => handleOrderClick(order.orderId)}>
                <span className="order-number">{order.orderId}</span>
                <span className="order-status">
                  {getOrderStatusText(order.orderStatus)}
                </span>
              </div>
              <div className="order-address" onClick={() => handleOrderClick(order.orderId)}>
                <div className="address-item">
                  <i className="iconfont icon-start-point"></i>
                  <span>{order.startName}</span>
                </div>
                <div className="address-item">
                  <i className="iconfont icon-end-point"></i>
                  <span>{order.destinationName}</span>
                </div>
              </div>
              <div className="order-time" onClick={() => handleOrderClick(order.orderId)}>
                <i className="iconfont icon-time"></i>
                <span>{order.departureTime}</span>
              </div>
              {order.flightInfo && (
                <div className="order-flight" onClick={() => handleOrderClick(order.orderId)}>
                  <i className="iconfont icon-flight"></i>
                  <span>{t('callOrder.label.flight')}: {order.flightInfo}</span>
                </div>
              )}
              {order.passengerPhone && (
                <div className="order-phone" onClick={() => handleOrderClick(order.orderId)}>
                  <i className="iconfont icon-phone"></i>
                  <span>{t('callOrder.label.phone')}: {order.passengerPhone}</span>
                </div>
              )}
              <div className="order-footer" onClick={() => handleOrderClick(order.orderId)}>
                <div className="order-price">
                  <span>{t('callOrder.label.amount')}:</span>
                  <span className="price">¥{order.price}</span>
                </div>
                <div className="payment-status">
                  {order.paymentStatus === 1 ? t('orderCard.payment.paid') : t('orderCard.payment.unpaid')}
                </div>
              </div>
              
              {/* 待服务订单显示操作按钮 */}
              {activeTab === 'pending' && (
                <div className="order-actions">
                  <button 
                    className="action-btn contact"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.location.href = `tel:${order.passengerPhone}`
                    }}
                  >
                    <img src={callImg} alt={t('callOrder.action.contactPassenger')} className="action-icon" />
                    <span>{t('callOrder.action.contactPassenger')}</span>
                  </button>
                  <button 
                    className="action-btn voice"
                    onClick={(e) => handlePlayVoice(order.voiceUrl, e)}
                  >
                    <img src={luyinImg} alt={t('callOrder.action.playVoice')} className="action-icon" />
                    <span>{t('callOrder.action.playVoice')}</span>
                  </button>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="empty-state">{t('callOrder.empty')}</div>
        )}
      </div>

      {/* 日期选择器 */}
      <DatePicker
        mode="date"
        visible={showDatePicker}
        value={selectedDate}
        onOk={handleDateConfirm}
        onDismiss={() => setShowDatePicker(false)}
      />
    </div>
  )
}

export default CallOrder
